import { ComponentProps, PropsWithChildren, useState } from "react";

import { default as classNames, default as cn } from "@/helpers/classNames";
import { ChevronDownIcon } from "lucide-react";

export default function DataBox(
  props: PropsWithChildren<{
    collapsed?: boolean;
    collapsible?: boolean;
    header: string;
    paddingless?: boolean;
    subheader?: string;
    action?: JSX.Element;
    loading?: boolean;
  }>,
) {
  const {
    children,
    collapsed = false,
    collapsible = false,
    header,
    paddingless = false,
    subheader,
    action,
  } = props;

  const [isCollapsed, setIsCollapsed] = useState(collapsed);

  return (
    <div className="flex-1">
      <div className="">
        <div
          className={classNames(
            !isCollapsed && "border-b",
            "text-gray-500 dark:border-gray-700 dark:text-white",
          )}
        >
          <div className="flex items-center justify-between">
            <div className="py-4">
              <p className="text-gray-800 dark:text-white">{header}</p>
              {subheader && (
                <p className="text-xs text-muted-foreground">{subheader}</p>
              )}
            </div>
            <div>
              {action && action}
              {collapsible && (
                <span
                  className="flex cursor-pointer items-center border-l py-4 dark:border-gray-700"
                  onClick={() => {
                    setIsCollapsed(!isCollapsed);
                  }}
                >
                  <ChevronDownIcon
                    className={classNames(
                      !isCollapsed && "rotate-180",
                      "h-5 transition-transform dark:text-white",
                    )}
                  />
                </span>
              )}
            </div>
          </div>
        </div>
        {!isCollapsed && (
          <div className={classNames(!paddingless && "py-4")}>{children}</div>
        )}
      </div>
    </div>
  );
}

export function DetailItem({
  title,
  children,
  ...props
}: ComponentProps<"div"> & {
  title: string;
}) {
  return (
    <div className={cn("flex items-center gap-2", props.className)}>
      <div
        className={cn(
          "w-40 shrink-0 text-sm whitespace-nowrap text-gray-700 dark:text-gray-400",
        )}
      >
        {title}
      </div>
      <div className="min-w-0 grow text-sm">
        {/* Keep the wrapper: Chrome translate reparents bare text nodes, and React then throws removing them. */}
        <div className="contents">{children}</div>
      </div>
    </div>
  );
}

export function Empty(props: PropsWithChildren) {
  return (
    <div className="flex h-32 flex-col items-center justify-center text-center text-gray-400">
      {props.children}
    </div>
  );
}
